import { inject, Injectable } from "@angular/core";
import { Functions } from "@angular/fire/functions";
import { httpsCallable } from "firebase/functions";
import {
  cloudFunctionNames,
  EntityStatuses,
  IBoirQueueItem,
  IBoirStatusResponse,
  IEntitySubmitConsent,
  IWebBoirFilingQueueItem,
  SubmissionStatuses,
  UUID,
} from "../interfaces";
import { MatDialog } from "@angular/material/dialog";
import { ActionDialogComponent, ActionDialogData } from "../components/action-dialog/action-dialog.component";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { Firestore } from "@angular/fire/firestore";
import { emailRegex } from "../utils/constants";
import { MatSnackBar } from "@angular/material/snack-bar";
import { v4 } from "uuid";
import { AlertsService } from "./alerts.service";

interface AccessToken {
  token: string;
  issuedOn: Date | null; // lasts for 1 hour
}

@Injectable({
  providedIn: "root",
})
export class BoirFilingsService {
  private functions: Functions = inject(Functions);
  private accessToken: AccessToken = {
    token: "",
    issuedOn: null,
  };
  private dialog: MatDialog = inject(MatDialog);
  private firestore = inject(Firestore);
  private snackbar = inject(MatSnackBar);
  private alertsService = inject(AlertsService);

  async enqueueBOIR(
    entityId: UUID,
    orgId: UUID | null,
    consentData: IEntitySubmitConsent
  ): Promise<UUID | null> {
    try {
      const processId = await this.initiateBOIR();

      if (!processId) {
        const error =
          "Could not get process ID from FinCEN API. Please try again.";
        this.snackbar.open(error, "Dismiss");
        this.alertsService.sendEmailAlert(error);
        throw new Error(error);
      }

      const boirFilingQueueRef = doc(
        this.firestore,
        "boirFilingQueue",
        processId
      );

      if (!boirFilingQueueRef) {
        const error = "Could not enqueue BOIR filing";
        this.snackbar.open(error, "Dismiss");
        throw new Error(error);
      }

      const queueItem: IBoirQueueItem = {
        entityId,
        consentData,
        submittedSuccessfully: false,
        createdAt: Timestamp.now(),
        processId,
        xmlUploadResponse: "",
        attachmentUploadResponses: [],
        orgId,
      };

      await setDoc(boirFilingQueueRef, queueItem, { merge: true });

      return processId;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async enqueueWebBOIRFiling(
    entityId: UUID,
    orgId: UUID | null = null,
    submittedSuccessfully = false
  ): Promise<UUID | null> {
    try {
      if (!entityId) {
        throw new Error('Entity ID is required');
      }

      const queueItem: IWebBoirFilingQueueItem = {
        entityId,
        orgId,
        submittedSuccessfully,
      }

      const filingId = v4();

      const webBoirFilingQueueDoc = doc(
        this.firestore,
        "webBoirFilingQueue",
        filingId
      );

      await setDoc(webBoirFilingQueueDoc, queueItem, { merge: true });

      return filingId;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  private async initiateBOIR() {
    try {
      const accessToken = await this.checkAccessToken();

      const initiateBOIRCallable = httpsCallable<
        { accessToken: string },
        string
      >(this.functions, cloudFunctionNames.boirProxy.initiateBOIR);

      const { data } = await initiateBOIRCallable({
        accessToken: accessToken.token,
      });

      if (!data) {
        throw new Error("Could not retrieve BOIR process ID");
      }

      return data;
    } catch (e) {
      console.error(e);
      return;
    }
  }

  async checkSubmissionStatus(processId: UUID) {
    const accessToken = await this.checkAccessToken();

    const checkSubmissionStatusCallable = httpsCallable<
      { processId: UUID; accessToken: string },
      IBoirStatusResponse
    >(this.functions, cloudFunctionNames.boirProxy.checkSubmissionStatus);

    const { data } = await checkSubmissionStatusCallable({
      processId,
      accessToken: accessToken.token,
    });

    return data;
  }

  async getTranscript(processId: string, entityId: string) {
    const transcriptFromStorage = await this.getTranscriptFromStorage(
      processId,
      entityId
    );

    if (transcriptFromStorage) {
      return transcriptFromStorage;
    }

    const accessToken = await this.checkAccessToken();

    const getTranscriptCallable = httpsCallable<
      { entityId: UUID; processId: UUID; accessToken: string },
      { pdfBinary: string; status: { submissionStatus: string } }
    >(this.functions, cloudFunctionNames.boirProxy.getTranscript);

    const { data } = await getTranscriptCallable({
      entityId,
      processId,
      accessToken: accessToken.token,
    });

    return data;
  }

  private async getTranscriptFromStorage(processId: UUID, entityId: UUID) {
    try {
      const getTranscriptCallable = httpsCallable<
        { entityId: UUID; processId: UUID },
        { pdfBinary: string }
      >(this.functions, cloudFunctionNames.boirAttachment.getTranscriptFromStorage);

      const { data } = await getTranscriptCallable({
        entityId,
        processId,
      });
      return data;
    } catch (e) {
      console.error(e);
      return;
    }
  }

  private async checkAccessToken() {
    if (
      this.accessToken.token === "" ||
      this.accessToken.issuedOn === null ||
      this.accessToken.issuedOn.getTime() < Date.now() - 3600000
    ) {
      return this.obtainAccessToken();
    } else {
      return this.accessToken;
    }
  }

  private async obtainAccessToken() {
    const obtainAccessTokenCallable = httpsCallable<null, string>(
      this.functions,
      "obtainAccessTokenCallable"
    );

    const { data } = await obtainAccessTokenCallable();

    this.accessToken = {
      token: data,
      issuedOn: new Date(),
    };

    return this.accessToken;
  }

  public openEntityOwnerInvalidDialog() {
    const data: ActionDialogData = {
      title: "Validation Error",
      positiveAction: false,
      positiveActionButtonText: "Return",
      message:
        "Signatory information must include valid email, first name, & last name. " +
        "If your owner is an entity, please select \"Entity is signatory\" and try again.",
    };

    this.dialog.open(ActionDialogComponent, { width: "350px", data });
  }

  public isSignatoryIsValid(consentData: IEntitySubmitConsent) {
    return (
      emailRegex.test(consentData.email) &&
      consentData.firstName &&
      consentData.lastName
    );
  }

  apiStatusToSubmissionStatus(apiStatus: SubmissionStatuses): EntityStatuses {
    switch (apiStatus) {
      case "submission_accepted":
        return "e-Filing Accepted";
      case "submission_pending":
        return "e-Filing Pending";
      case "submission_rejected":
      case "submission_validation_failed":
        return "e-Filing Rejected";
      default:
        return "e-Filing Pending";
    }
  }
}
